import log from 'loglevel';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { waitForSceneReady } from '../appContext';
import {
  setSceneIsLoading,
  setStyleNotFoundMessage,
  setStylePreviewUrl,
  setStyleType,
  setMockupLogos,
  clearMockupLogos,
} from '../store/appSlice';
import { setSceneFromStyleAggregatorData } from '../actions/setScene';
import { fetchStyleAggregatorData, StyleNotFoundError } from '../api/styleAggregator';
import { MeasurementSystem } from '@seddi/core/lib/cjs';

export type ViewerControlsLayout = 'tr' | 'tl' | 'bl' | 'br';

export type ViewerControlsStyle = {
  textColor?: string;
  backgroundColor?: string;
  borderColor?: string;
};

const DEFAULT_CONTROLS_LAYOUT: ViewerControlsLayout = 'tl';

const isValidHexColor = (value: unknown): value is string =>
  typeof value === 'string' && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value);

const DEFAULT_UNITS: MeasurementSystem = MeasurementSystem.Imperial;

const parseUnits = (value: string | null): MeasurementSystem => {
  const v = value?.toLowerCase();
  if (v === 'imperial') return MeasurementSystem.Imperial;
  if (v === 'metric') return MeasurementSystem.Metric;
  return DEFAULT_UNITS;
};

export const useUnitsFromUrl = (): MeasurementSystem => {
  const [searchParams] = useSearchParams();
  return parseUnits(searchParams.get('units')) ?? DEFAULT_UNITS;
};

export const useShowGraphicsPanelFromUrl = (): boolean => {
  const [searchParams] = useSearchParams();
  return searchParams.get('logos') === 'true';
};

export type ViewerIdleAnimationMode = 'static' | 'rotate';

const parseIdleAnimationMode = (raw: string | null): ViewerIdleAnimationMode => {
  const v = raw?.toLowerCase();
  if (v === 'rotate') return 'rotate';
  return 'static';
};

export const useIdleAnimationModeFromUrl = (): ViewerIdleAnimationMode => {
  const [searchParams] = useSearchParams();
  return parseIdleAnimationMode(searchParams.get('idle'));
};

const parseControlsLayout = (value: string | null): ViewerControlsLayout => {
  if (value === 'tl' || value === 'tr' || value === 'bl' || value === 'br') {
    return value;
  }

  return DEFAULT_CONTROLS_LAYOUT;
};

const parseControlsStyle = (raw: string | null): ViewerControlsStyle | undefined => {
  if (!raw) {
    return undefined;
  }

  try {
    const decoded = decodeURIComponent(raw);
    const parsed = JSON.parse(decoded) as {
      t?: unknown;
      textColor?: unknown;
      bg?: unknown;
      backgroundColor?: unknown;
      bd?: unknown;
      borderColor?: unknown;
    };

    const textColorSource = (parsed.t ?? parsed.textColor) as unknown;
    const backgroundColorSource = (parsed.bg ?? parsed.backgroundColor) as unknown;
    const borderColorSource = (parsed.bd ?? parsed.borderColor) as unknown;

    const textColor = isValidHexColor(textColorSource) ? textColorSource : undefined;
    const backgroundColor = isValidHexColor(backgroundColorSource)
      ? backgroundColorSource
      : undefined;
    const borderColor = isValidHexColor(borderColorSource) ? borderColorSource : undefined;

    if (!textColor && !backgroundColor && !borderColor) {
      return undefined;
    }

    return {
      textColor,
      backgroundColor,
      borderColor,
    };
  } catch (error) {
    log.warn('Failed to parse "controls_style" query parameter.', error);
    return undefined;
  }
};

export const useStyleFromUrl = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [brandLogoPolicy, setBrandLogoPolicy] = useState({
    isMockupMode: false,
    useBrandLogos: false,
  });

  useEffect(() => {
    const controller = new AbortController();
    const apiKey = searchParams.get('api_key') ?? '';
    const brand = searchParams.get('brand') ?? '';
    const styleNumber = searchParams.get('style_number') ?? '';
    const colorway = searchParams.get('colorway') ?? undefined;
    const mockupId = searchParams.get('mockup_id') ?? undefined;

    const isMockupMode = Boolean(mockupId);
    setBrandLogoPolicy({
      isMockupMode,
      useBrandLogos: false,
    });

    if (!apiKey) {
      log.warn('No api key provided or missing required query params. Skipping style load.');
      dispatch(setStyleNotFoundMessage('Missing required "api_key" query parameter.'));
      return () => {
        controller.abort();
      };
    }

    if (!isMockupMode) {
      if (!brand || !styleNumber || !colorway) {
        log.error('Missing required query params "brand", "style_number" and/or "colorway".');
        dispatch(
          setStyleNotFoundMessage(
            'Missing required query parameters "brand", "style_number" and/or "colorway".',
          ),
        );
        return () => {
          controller.abort();
        };
      }
    }

    const load = async () => {
      try {
        dispatch(setSceneIsLoading(true));
        dispatch(setStylePreviewUrl(null));
        dispatch(setStyleNotFoundMessage(null));

        const data = await fetchStyleAggregatorData(
          isMockupMode
            ? {
                apiKey,
                mockupId: mockupId as string,
                signal: controller.signal,
              }
            : {
                apiKey,
                brand,
                styleNumber,
                colorway,
                signal: controller.signal,
              },
        );

        const previewUrl =
          (data.mockup as any)?.preview ?? ((data.style as any).preview as string | undefined);
        dispatch(setStylePreviewUrl(previewUrl ?? null));

        setBrandLogoPolicy({
          isMockupMode,
          useBrandLogos: isMockupMode ? Boolean((data.mockup as any)?.useBrandLogos) : false,
        });

        const styleType = (data.style as any).type as string | undefined;
        dispatch(setStyleType(styleType ?? null));

        const editorSceneLogos = (data.mockup as any)?.liveblocksData?.editorSceneLogos;
        const hideTearAwayLabel = Boolean(editorSceneLogos?.hideTearAwayLabel);

        if (editorSceneLogos?.logos && editorSceneLogos.logos.length > 0) {
          dispatch(
            setMockupLogos({
              logos: editorSceneLogos.logos,
              hideTearAwayLabel,
            }),
          );
        } else {
          dispatch(clearMockupLogos());
        }

        const effectiveUseBrandLogos = isMockupMode
          ? Boolean((data.mockup as any)?.useBrandLogos)
          : true;
        await setSceneFromStyleAggregatorData(
          dispatch,
          data,
          hideTearAwayLabel,
          effectiveUseBrandLogos,
        );
        await Promise.race([
          waitForSceneReady(),
          new Promise<void>(resolve => setTimeout(resolve, 15000)),
        ]);
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          return;
        }

        if (error instanceof StyleNotFoundError) {
          log.error(error.message);
          dispatch(setStyleNotFoundMessage(error.message));
          dispatch(setStyleType(null));
          return;
        }

        log.error('Error loading style from URL:', error);
      } finally {
        dispatch(setSceneIsLoading(false));
      }
    };

    void load();

    return () => {
      controller.abort();
    };
  }, [dispatch, searchParams]);

  return brandLogoPolicy;
};

export const useViewerControlsFromUrl = (): {
  controlsLayout: ViewerControlsLayout;
  controlsStyle?: ViewerControlsStyle;
} => {
  const [searchParams] = useSearchParams();

  const controlsLayoutRaw = searchParams.get('controls_layout');
  const controlsStyleRaw = searchParams.get('controls_style');

  const controlsLayout = parseControlsLayout(controlsLayoutRaw);
  const controlsStyle = parseControlsStyle(controlsStyleRaw);

  return {
    controlsLayout,
    controlsStyle,
  };
};
